import React, { useEffect, useContext, useState, useRef } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import ReportExcelPrintGap from "../../../components/Reports/ReportExcelPrintGap";

let groupArr = [
  { id: 1, name: "Document No" },
  { id: 2, name: "Document Date" },
  { id: 3, name: "Supplier" },
  { id: 4, name: "Branch" }
];

const PurchaseReturnReport = () => {
  const { t } = useTranslation();
  //getting context values here
  let {
    loading,
    dataPaginating,
    setDataPaginating,
    generalSettings
  } = useContext(SettingsContext);
  const { authUserInfo, supplierForSearch, getSupplier } = useContext(UserContext);
  const { branchForSearch } = useContext(RestaurantContext);

  //all data
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [branch, setBranch] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [group, setGroup] = useState(groupArr[3]);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");
  const [reportOption, setReportOption] = useState("detail");
  
  useEffect(() => {
    if(generalSettings && !supplierForSearch){
      getSupplier()
    }
  }, [generalSettings]);

  let common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  const handleSetType = (e) => {
    setReportOption(e.target.value)
  }

  const handleOption = (e) => {
    setOption(e.target.value);
  };

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `
        @page {
          margin: 15px 7px;
        }
      `,
  });

  //get excel reports filter
  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (startDate !== null && endDate !== null && group !== null) {
      getReport();
    } else {
      toast.error(
        `${_t(t("Please select from date, to date and group"))}`,
        common_toast
      );
    }
  };

  //get report
  const getReport = async () => {
    if (
      (authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager") &&
      !branch?.id
    ) {
      toast.error(`${_t(t("Please select branch to generate report"))}`, common_toast);
      return;
    }

    let fromDate = moment(startDate).format("YYYY-MM-DD");
    let toDate = moment(endDate).format("YYYY-MM-DD");
    const url = BASE_URL + "/settings/get-purchase-return-report";
    setDataPaginating(true);
    let formData = new FormData();
    formData.append("fromDate", fromDate);
    formData.append("toDate", toDate);
    formData.append("branch_id", branch ? branch.id : "");
    formData.append("supplier_id", supplier ? supplier.id : "");
    formData.append("group_by", group.name);
    formData.append("type", reportOption);

    return axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setGeneratedReport(true);
        setReportData(res.data);
        setDataPaginating(false);
        if (option === "excel") {
          componentRef.current.handleDownload();
        } else {
          thermalPrint();
        }
      })
      .catch((error) => {
        setDataPaginating(false);
        toast.error(`${_t(t("Please try again"))}`, common_toast);
      });
  };

  const headerSummary = [
    { name: "Group Name", key: "grp" },
    { name: "Document No", key: "document_no" },
    { name: "Invoice Number", key: "invoice_number" },
    { name: "Document Date", render: (item) => <Moment format="LL">{item.document_date}</Moment> },
    { name: "Payment Type", key: "payment_type" },
    { name: "Branch", key: "branch_name" },
    { name: "Supplier Name", key: "supplier_name" },
    { name: "Total Amount", key:"total_amount", total: 1},
    { name: "Total Discount", key:"total_discount" ,total: 1},
    { name: "Total Tax", key:"total_tax" ,total: 1},
    { name: "Total Bill", key:"total_bill" ,total: 1},
    { name: "Is Post", render: (item) => item.is_post ? "Yes" : "No" },
  ];

  const headerDetail = [
    { name: "Group Name", key: "grp" },
    { name: "Document No", key: "document_no" },
    { name: "Invoice Number", key: "invoice_number" },
    { name: "Document Date", render: (item) => <Moment format="LL">{item.document_date}</Moment> },
    { name: "Payment Type", key: "payment_type" },
    { name: "Branch", key: "branch_name" },
    { name: "Supplier Name", key: "supplier_name" },
    { name: "Ingredient", key: "ingredient_name" },
    { name: "Unit", key: "unit" },
    { name: "Qty", key:"qty", total: 1},
    { name: "Rate", key:"rate" ,total: 1},
    { name: "Amount", key:"amount" ,total: 1},
    { name: "Discount", key:"discount" ,total: 1},
    { name: "Tax", key:"tax" ,total: 1},
    { name: "Total", key:"total" ,total: 1},
    { name: "Is Post", render: (item) => item.is_post ? "Yes" : "No" },
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Purchase Return Report"))}</title>
      </Helmet>
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename={"Purchase Return Report"}
        sheet="sheet 1"
        buttonText="Download"
        ref={componentRef}
      />

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Purchase Return Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end align-items-center">
                            <div className="d-flex bg-light rounded p-1 mr-3 border">
                              <button
                                type="button"
                                className={`btn btn-sm border-0 ${reportOption === 'detail' ? 'btn-primary shadow-sm rounded' : 'text-muted'}`}
                                onClick={() => handleSetType({ target: { value: 'detail' } })}
                                style={{ transition: 'all 0.2s', padding: '0.25rem 1rem' }}
                              >
                                <i className="fa fa-list mr-1"></i> {_t(t("Detail"))}
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm border-0 ${reportOption === 'summary' ? 'btn-primary shadow-sm rounded' : 'text-muted'}`}
                                onClick={() => handleSetType({ target: { value: 'summary' } })}
                                style={{ transition: 'all 0.2s', padding: '0.25rem 1rem' }}
                              >
                                <i className="fa fa-pie-chart mr-1"></i> {_t(t("Summary"))}
                              </button>
                            </div>
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-2 d-md-block">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-2 d-md-block">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>
                        <div className="col-2">
                          <Select
                            options={branchForSearch || []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={branch}
                            styles={customStyle}
                            isClearable
                            classNamePrefix="select"
                            className="table-text"
                            onChange={(branch) => setBranch(branch)}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select branch")) + ".."}
                          />
                        </div>

                        <div className="col-2">
                          <Select
                            options={supplierForSearch ? supplierForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            classNamePrefix="select"
                            value={supplier}
                            className="table-text"
                            styles={customStyle}
                            onChange={(supplier) => setSupplier(supplier)}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select supplier")) + ".."}
                            isClearable
                          />
                        </div>
                        
                        <div className="col-2">
                          <Select
                            options={groupArr && groupArr}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={group}
                            styles={customStyle}
                            isClearable
                            classNamePrefix="select"
                            className="table-text"
                            onChange={(group) => setGroup(group)}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select group")) + ".."}
                          />
                        </div>

                        <div className="col-2 t-mb-15 mb-md-0 text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={(e) => getExcelReport()}
                          >
                            GENERATE REPORT
                          </button>
                        </div>
                      </div>
                      <div className="row mt-2">

                        <div className="col"></div>

                      </div>
                      <ReportExcelPrintGap
                        header={reportOption === "summary" ? headerSummary : headerDetail}
                        generatedReport = {generatedReport}
                        heading={reportOption === "summary" ? "Purchase Return Summary Report" : "Purchase Return Report"}
                        reportData={reportData}
                        startDate={startDate}
                        endDate={endDate}
                        componentRef={componentRef}
                        printRef={componentRefThermal}
                        branch={{
                          name: branch ? (Array.isArray(branch) ? branch.map((item) => item.name).join(", ") : branch.name) : "All Branches",
                          show: true,
                          removeTotal: true
                        }}
                        type={option}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default PurchaseReturnReport;
